import type { ComponentProps } from "react";

import type { VerificationRequiredDialog } from "@layout/Topbar/VerificationRequiredDialog";
import type { BlockUserDialog } from "@layout/UserList/BlockUserDialog";
import type { MuteUserDialog } from "@layout/UserList/MuteUserDialog";
import { create } from "zustand";

import type { CustomStreamDialog } from "@screens/BroadcastSettings/components/CustomStreamDialog";
import type { CustomDatesDialog } from "@screens/Earnings/components/CustomDatesDialog";
import type { EditPostDialog } from "@screens/Posts/components/EditPostDialog";
import type { PostSentToModerationDialog } from "@screens/Posts/components/PostSentToModerationDialog";
import type { RejectedPostDialog } from "@screens/Posts/components/RejectedPostDialog";
import type { DeleteRemarkDialog } from "@screens/Remarks/components/DeleteRemarkDialog";
import type { UpdateRemarkDialog } from "@screens/Remarks/components/UpdateRemarkDialog";
import type { FinishTourDialog } from "@screens/ShowSettings/components/FinishTourDialog";

import type { DeleteGoalDialog } from "@features/goals/DeleteGoalDialog";
import type { GoalSettingsDialog } from "@features/goals/GoalSettingsDialog";
import type { MessageLinkDialog } from "@features/messenger/ChatContainer/MessageElement/MessageLinkDialog";
import type { PermissionDisabledDialog } from "@features/permission/PermissionDisabledDialog";
import type { PermissionEnabledDialog } from "@features/permission/PermissionEnabledDialog";

export const DIALOG_KEYS = {
	BLOCK_USER: "BLOCK_USER",
	MUTE_USER: "MUTE_USER",
	MESSAGE_LINK: "MESSAGE_LINK",
	VERIFICATION_REQUIRED: "VERIFICATION_REQUIRED",
	PERMISSION_ENABLED: "PERMISSION_ENABLED",
	PERMISSION_DISABLED: "PERMISSION_DISABLED",
	UPDATE_REMARK: "UPDATE_REMARK",
	DELETE_REMARK: "DELETE_REMARK",
	TIP_MENU_ACTIVITY: "TIP_MENU_ACTIVITY",
	FINISH_TOUR: "FINISH_TOUR",
	CUSTOM_DATES: "CUSTOM_DATES",
	CUSTOM_STREAM: "CUSTOM_STREAM",
	EDIT_POST: "EDIT_POST",
	POST_SENT_TO_MODERATION: "POST_SENT_TO_MODERATION",
	REJECTED_POST: "REJECTED_POST",
	GOAL_SETTINGS: "GOAL_SETTINGS",
	DELETE_GOAL: "DELETE_GOAL",
} as const;

export type DialogKey = (typeof DIALOG_KEYS)[keyof typeof DIALOG_KEYS];

type EmptyDialogProps = Record<PropertyKey, never>;
type InjectedDialogProp = "onClose";
type PublicDialogProps<TProps> = keyof TProps extends never ? EmptyDialogProps : TProps;

export type DialogComponentPropsByKey = {
	[DIALOG_KEYS.BLOCK_USER]: ComponentProps<typeof BlockUserDialog>;
	[DIALOG_KEYS.MUTE_USER]: ComponentProps<typeof MuteUserDialog>;
	[DIALOG_KEYS.MESSAGE_LINK]: ComponentProps<typeof MessageLinkDialog>;
	[DIALOG_KEYS.VERIFICATION_REQUIRED]: ComponentProps<typeof VerificationRequiredDialog>;
	[DIALOG_KEYS.PERMISSION_ENABLED]: ComponentProps<typeof PermissionEnabledDialog>;
	[DIALOG_KEYS.PERMISSION_DISABLED]: ComponentProps<typeof PermissionDisabledDialog>;
	[DIALOG_KEYS.UPDATE_REMARK]: ComponentProps<typeof UpdateRemarkDialog>;
	[DIALOG_KEYS.DELETE_REMARK]: ComponentProps<typeof DeleteRemarkDialog>;
	[DIALOG_KEYS.TIP_MENU_ACTIVITY]: EmptyDialogProps;
	[DIALOG_KEYS.FINISH_TOUR]: ComponentProps<typeof FinishTourDialog>;
	[DIALOG_KEYS.CUSTOM_DATES]: ComponentProps<typeof CustomDatesDialog>;
	[DIALOG_KEYS.CUSTOM_STREAM]: ComponentProps<typeof CustomStreamDialog>;
	[DIALOG_KEYS.EDIT_POST]: ComponentProps<typeof EditPostDialog>;
	[DIALOG_KEYS.POST_SENT_TO_MODERATION]: ComponentProps<typeof PostSentToModerationDialog>;
	[DIALOG_KEYS.REJECTED_POST]: ComponentProps<typeof RejectedPostDialog>;
	[DIALOG_KEYS.GOAL_SETTINGS]: ComponentProps<typeof GoalSettingsDialog>;
	[DIALOG_KEYS.DELETE_GOAL]: ComponentProps<typeof DeleteGoalDialog>;
};

export type DialogPropsByKey = {
	[TKey in DialogKey]: PublicDialogProps<Omit<DialogComponentPropsByKey[TKey], InjectedDialogProp>>;
};

type DialogProps = DialogPropsByKey[DialogKey];
type OpenDialogArgs<TKey extends DialogKey> = DialogPropsByKey[TKey] extends EmptyDialogProps
	? [type: TKey, props?: DialogPropsByKey[TKey]]
	: [type: TKey, props: DialogPropsByKey[TKey]];

type DialogStore = {
	dialogType: DialogKey | null;
	dialogProps: DialogProps;
	dialogId: number;
	isOpen: boolean;
	openDialog: <TKey extends DialogKey>(...args: OpenDialogArgs<TKey>) => void;
	closeDialog: (dialogId?: number) => void;
};

export const useDialogStore = create<DialogStore>()((set) => ({
	dialogType: null,
	dialogProps: {},
	dialogId: 0,
	isOpen: false,
	openDialog: ((type: DialogKey, props?: DialogProps) =>
		set((state) => {
			if (state.isOpen) {
				return state;
			}

			return {
				dialogType: type,
				dialogProps: props ?? {},
				dialogId: state.dialogId + 1,
				isOpen: true,
			};
		})) as DialogStore["openDialog"],
	closeDialog: (dialogId) =>
		set((state) => {
			if (dialogId !== undefined && state.dialogId !== dialogId) {
				return state;
			}

			return { dialogType: null, dialogProps: {}, isOpen: false };
		}),
}));
